<script setup>
import { computed, reactive, ref, watch } from "vue";
import { trans } from "i18n";
import { getErrorMessage } from "lib/modules/util";
import Http from "lib/modules/Http";

const props = defineProps(["cachedEmail", "readonlyEmail"]);
const emit = defineEmits(["activeComponent", "login", "update:busy", "update:cachedEmail"]);

const http = reactive(new Http({}));
const data = reactive({
	email: props.cachedEmail,
	lang_id: document.documentElement.lang
});
const verifyPassword = ref("");

const passwordError = computed(() => {
	return data.password !== verifyPassword.value && verifyPassword.value.length > 0 ? trans.gettext("Password mismatch") : "";
});

function reset() {
	http.post("/api/pwreset", data, () => {
		if (http.data.reset_token)
			data.token = http.data.reset_token;
		else
			emit("login", data.email);
	},
	() => {
		if (http.error === "token expired") {
			delete data.key;
			delete data.token;
			http.error = trans.gettext("The verification code has expired, click '%1' to get a new code.", trans.gettext("Reset"));
		}
	});
}

watch(() => http.busy, (val) => emit("update:busy", val));
</script>

<template>
	<form @submit.prevent="reset()">
		<h2>{{ $gettext("Reset password") }}</h2>
		<fieldset :disabled="http.busy" class="grid gap-8">
			<div v-if="!data.token" class="grid gap-16">
				<UiInput
					v-model="data.email"
					v-focus="!readonlyEmail"
					:label="$gettext('E-mail')"
					:readonly="readonlyEmail"
					autocomplete="email"
					type="email"
					required
					@update:model-value="$emit('update:cachedEmail', email)"
				/>
				<div class="grid gap-16">
					<UiInput
						v-focus="readonlyEmail"
						:value="data.password"
						:label="$gettext('Password')"
						:placeholder="$gettext('New password')"
						autocomplete="new-password"
						minlength="8"
						type="password"
						required
						@input="data.password = $event.target.value"
					/>
					<UiInput
						:value="verifyPassword"
						:error="passwordError"
						:placeholder="$gettext('Verify new password')"
						autocomplete="new-password"
						type="password"
						required
						@input="verifyPassword = $event.target.value"
					/>
				</div>
			</div>
			<div v-else class="grid gap-16">
				<p>{{ $gettext("A verification code has been sent to your e-mail address %1. Please enter this code below.", data.email) }}</p>
				<UiInput
					v-model="data.key"
					:label="$gettext('Verification code')"
					autocomplete="one-time-code"
					type="text"
					required
				/>
			</div>
			<div class="vui-error justify-self-end">{{ getErrorMessage(http.error) }}</div>
			<div class="grid cols-2 gap-16">
				<button type="button" class="vui-btn outlined icon-left justify-self-start" @click="$emit('activeComponent', 'Login')">
					<i class="fa-regular fa-arrow-left"></i>
					<span>{{ $gettext("Login") }}</span>
				</button>
				<button type="submit" class="vui-btn justify-self-end">{{ $gettext("Reset") }}</button>
			</div>
		</fieldset>
	</form>
</template>
